/**
 * The sea, and every colour derived from it. Every number is labelled with where it came from.
 *
 * MEASURED numbers are read off a confirmed reference frame by an instrument, and the frame
 * count is stated. DERIVED numbers are arithmetic on measured ones. RULING is a choice the
 * measurement left open, made once. Two living-sea documents are the specs these implement, at
 * grain 2: gauntlet/living-sea/OPEN-WATER.md section 4 for the open water (POKOMOKO) and
 * gauntlet/living-sea/OBJECT-WATERLINE.md section 7 for the shoreline (Eastward).
 */

export type Rgb = readonly [number, number, number];

/**
 * OPEN WATER: POKOMOKO's four values, verbatim. CONTRACT 1.5g(a) rules POKOMOKO the governor
 * of open water; gauntlet/living-sea/OPEN-WATER.md section 3.2 MEASURED exactly four RGBA values
 * in the lossless asset (n = 16,384 px, codec floor 0), and section 4.2 rule 1 says a renderer
 * draws exactly those four and nothing between them.
 *
 *   base       luma  61.6           79.3% of the tile
 *   mid        luma 135.6  +74.0    10.5%
 *   crest      luma 225.1  +163.5    9.1%
 *   highlight  luma 235.8  +174.2    1.1%, and only ever inside the crest line
 *
 * WHY THE BASE MOVED, and it is a RULING the spec forced rather than a taste. Rule 1 writes the
 * values as W, W+74, W+163.5, W+174.2 with W the game's own water base. Until round 4 that base
 * was Eastward's, MEASURED as the mode of 3x3-block means over 688,780 px of eastward-water-00:
 * (36, 112, 168), luma 99.9. White is 255, so 99.9 leaves 155.1 luma of headroom and the crest at
 * +163.5 and the highlight at +174.2 DO NOT FIT. Compressing the deltas to fit is the conversion
 * rule 1 forbids ("luma figures take NO conversion"). So W is the reference's own base. The
 * Eastward figure survives below as CHART_ANCHOR_RGB, because a chart was tuned by eye on it.
 */
export const SEA_RGB: Rgb = [11, 72, 107];
export const WATER_MID_RGB: Rgb = [66, 151, 188];
export const WATER_CREST_RGB: Rgb = [199, 232, 234];
export const WATER_HIGHLIGHT_RGB: Rgb = [212, 242, 245];
/** In luma order. The baked tile (src/render/baked/water.png) contains these four and no other. */
export const WATER_RGBS: readonly Rgb[] = [SEA_RGB, WATER_MID_RGB, WATER_CREST_RGB, WATER_HIGHLIGHT_RGB];

/**
 * THE SEA THE CHART WAS TUNED AGAINST. Eastward's water, MEASURED as above, and the value every
 * chart scalar below was SET BY LOOKING at. When the open water moved to POKOMOKO's base the
 * chart's colours were pinned here rather than silently re-derived from a sea nobody has looked
 * at them on: paper, ink and wash still come from this anchor. Re-tune by eye against the new sea
 * if wanted; nothing else reads it.
 */
export const CHART_ANCHOR_RGB: Rgb = [36, 112, 168];

/**
 * THE SEA BASE IS "SOMEWHERE BETWEEN" (CONTRACT 1.5i(c)): between POKOMOKO's navy and Eastward's
 * blue, tuned by the owner's eye through `?sea=0..1` on world.html. 0 is the navy round 4 shipped
 * on, which is the tile's own base, so at 0 and full amplitude the four values are POKOMOKO's
 * verbatim; 1 is the Eastward blue the chart was tuned on. SEA_BLEND_DEFAULT is a GUESS that
 * splits the difference until the owner picks; once picked it is a RULING, not a measurement.
 */
export const SEA_NAVY_RGB: Rgb = SEA_RGB;
export const SEA_EASTWARD_RGB: Rgb = CHART_ANCHOR_RGB;
export const SEA_BLEND_DEFAULT = 0.5;

const clamp255 = (v: number) => Math.max(0, Math.min(255, Math.round(v)));

/** The base at blend `b`: navy at 0, Eastward at 1, whole channels between. */
export function seaBase(blend: number): Rgb {
  const b = Math.min(1, Math.max(0, blend));
  return [0, 1, 2].map((i) => clamp255(SEA_NAVY_RGB[i]! + (SEA_EASTWARD_RGB[i]! - SEA_NAVY_RGB[i]!) * b)) as unknown as Rgb;
}

/**
 * THE FOUR OPEN-WATER VALUES on a given base at a given amplitude, in luma order. Each is the
 * base plus POKOMOKO's per-channel delta over its own base, scaled by two things:
 *
 *   the AMPLITUDE the field asks for, 0..1 (water.ts: 1 at a shore and at the boat, the calm
 *   floor on the open sea), and
 *   the base's LUMA HEADROOM under white relative to the navy's, so a lighter base compresses
 *   the crest and the highlight to fit (1.5i(c): "the crest and highlight deltas rescale to fit
 *   it"). Navy has 193.4 luma of headroom and the highlight uses 174.2 of it; Eastward's blue has
 *   155.1, so its deltas scale by 0.80 and the highlight lands at luma 238.
 *
 * On the navy base at amplitude 1 the result is the asset's four values exactly; at amplitude 0
 * all four are the base. A channel that would still overflow is clamped, which costs the
 * highlight's blue a few units on the lightest bases and nothing else.
 */
export function waterPalette(base: Rgb, amplitude: number): readonly [Rgb, Rgb, Rgb, Rgb] {
  const headroom = (255 - luma(base)) / (255 - luma(SEA_NAVY_RGB));
  const k = Math.min(1, Math.max(0, amplitude)) * Math.min(1, headroom);
  const on = (c: Rgb): Rgb => [0, 1, 2].map((i) => clamp255(base[i]! + (c[i]! - SEA_NAVY_RGB[i]!) * k)) as unknown as Rgb;
  return [base, on(WATER_MID_RGB), on(WATER_CREST_RGB), on(WATER_HIGHLIGHT_RGB)];
}

export interface Hsv {
  readonly h: number;
  readonly s: number;
  readonly v: number;
}

export function rgbToHsv([r, g, b]: Rgb): Hsv {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

/** `v` on the 0..255 scale, as `rgbToHsv` returns it. Rounded to whole channels. */
export function hsvToRgb(h: number, s: number, v: number): Rgb {
  const c = v * s;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 1) [r, g, b] = [c, x, 0];
  else if (hh < 2) [r, g, b] = [x, c, 0];
  else if (hh < 3) [r, g, b] = [0, c, x];
  else if (hh < 4) [r, g, b] = [0, x, c];
  else if (hh < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = v - c;
  return [Math.round(r + m), Math.round(g + m), Math.round(b + m)];
}

const anchor = rgbToHsv(CHART_ANCHOR_RGB);

/**
 * THE RIM. One art pixel on the water, touching the shadow, at the visible contact.
 *   +46 on value: MEASURED, per-ray conditional, +46.2 and +45.0 on the two clean reference
 *        frames (n=2, JPEG-bounded).
 *   saturation x0.45: MEASURED, x0.517 and x0.376 at the peak on the same two frames; the mean.
 *   hue held: RULING (within 3 degrees on eastward-water-00; the night frames' warm shift is not adopted).
 *   1 px: MEASURED 1.0 px at grain 3, RULING 1 px at grain 2 ("Eastward but sharper" is the thinner one).
 * The three figures are RELATIVE to the water they sit on, so the rim is a function of the base:
 * `rimFor(seaBase(blend))` at runtime, and RIM_RGB is that function on the navy.
 */
export function rimFor(base: Rgb): Rgb {
  const s = rgbToHsv(base);
  return hsvToRgb(s.h, s.s * 0.45, Math.min(255, s.v + 46));
}
export const RIM_RGB: Rgb = rimFor(SEA_RGB);

/**
 * THE CONTACT SHADOW, on the water below the rim, multiplicative on value, hue and saturation held.
 * Deepest x0.80: DERIVED from x0.791 (bank) and x0.812 (hulls), MEASURED on n=2 clean frames.
 * Shape: the octopia-01 ramp, six MEASURED per-offset values re-anchored to the 0.80 floor and
 * resampled from 6 px at grain 3 to 9 px at grain 2 (OBJECT-WATERLINE section 7b). Width 9 sits
 * inside the MEASURED bracket 8-12 (6 and 8 px at grain 3, x1.5). Six distinct values, no invented steps.
 */
export const SHADOW_RAMP: readonly number[] = [0.901, 0.806, 0.806, 0.8, 0.83, 0.83, 0.874, 0.94, 0.94];

export function scaleRgb([r, g, b]: Rgb, k: number): Rgb {
  return [Math.round(r * k), Math.round(g * k), Math.round(b * k)];
}

/** The ramp on the base value alone: what a plate composited on a flat sea shows. */
export const SHADOW_RGBS: readonly Rgb[] = SHADOW_RAMP.map((k) => scaleRgb(SEA_RGB, k));
/**
 * The same ramp as 8-bit MULTIPLY factors, for drawing over water that is no longer a constant
 * fill. "Multiplicative on value, hue and saturation held" IS a per-channel multiply by k, so a
 * grey (k, k, k) sprite in multiply blend applies the measured treatment to whatever the water
 * shows there - base, mid or crest - instead of replacing it with a flat patch. On a base pixel
 * the result is SHADOW_RGBS to within the 8-bit rounding of k (0.901 -> 230/255 = 0.902).
 */
export const SHADOW_FACTORS: readonly number[] = SHADOW_RAMP.map((k) => Math.round(k * 255));

/**
 * THE CHART'S OWN COLOURS. Every one is arithmetic on the sea the chart was tuned on
 * (CHART_ANCHOR_RGB, above), and the arithmetic is here so the strip can never drift into a
 * palette no sea of this game has contained.
 *
 * THE PAPER IS NOT #F4F1EA, and that is a decision rather than an accident. The measured
 * design corpus names a near-#F4F1EA cream as one of exactly three looks generated design
 * lands on whatever the subject is, so a chart lying on this sea may not be that value.
 * It is derived by rotating the sea's own hue and desaturating:
 *
 *   hue         sea.h + 205 = 50.5 deg. RULING, and the 25 degrees past the complement are
 *               the whole point. THE COMPLEMENT ITSELF (25.5 deg) IS SAND - it is the hue
 *               the shore sprites are already made of - and at three separate values a strip
 *               of it across the bottom of a blue field read as a BEACH rather than as paper.
 *               Twenty-five degrees on carries the same warmth into khaki, which is what
 *               chart stock and manila actually are, and no island in the frame is that hue.
 *   saturation  sea.s x 0.26 = 0.204. RULING.
 *   value       sea.v x 1.10 = 185. RULING.
 *
 * That lands on rgb(185, 179, 147). Every one of those three scalars was SET BY LOOKING at
 * the build in a browser at 1920x1080 rather than reasoned about in the abstract, and the
 * two earlier attempts - x4/3 of value at the complement, then x1.22 - are recorded here
 * because both were rejected by the same eye for the same reason. Against the AI cream it is
 * 60 units darker at four times the chroma.
 *
 * THE INK IS THE SEA, DARKENED, not black: hue held, saturation raised a seventh, value at
 * 15.5% - rgb(3, 16, 26). A pure black on this paper reads as printed; a sea-tinted
 * near-black reads as written in the light the rest of the frame is lit by.
 */
export const CHART_PAPER_RGB: Rgb = hsvToRgb(anchor.h + 205, anchor.s * 0.26, anchor.v * 1.1);
/** One step down for the fibre, so the paper has a material and is not a flat fill. */
export const CHART_GRAIN_RGB: Rgb = scaleRgb(CHART_PAPER_RGB, 0.955);
/** The rules of the ledger and the shadow under the torn edge. */
export const CHART_SHADE_RGB: Rgb = scaleRgb(CHART_PAPER_RGB, 0.86);
/**
 * THE DECKLE: the paper's own thickness, seen along the tear.
 *
 * DARKER than the sheet, and it took two goes to settle. Drawn much darker (x0.70) the edge
 * read as wet sand; drawn LIGHTER (x1.11) it read as surf, which was worse - a bright broken
 * fringe along a warm band is exactly what a breaking wave looks like. A shallow darkening is
 * the one reading that says thickness without saying water.
 */
export const CHART_DECKLE_RGB: Rgb = scaleRgb(CHART_PAPER_RGB, 0.84);
export const CHART_INK_RGB: Rgb = hsvToRgb(anchor.h, Math.min(1, anchor.s * 1.15), anchor.v * 0.155);
/**
 * Ink let down with water: the second written role's colour, and the ledger's locked rows.
 *
 * THE SCALAR HAS MOVED TWICE AND BOTH MOVES WERE THE SAME BAR, ONE PAPER APART.
 *
 * 0.62 scored 4.49 against the PAPER and AA wants 4.5, so it became 0.58 (4.841 on paper).
 * That left the FIBRE GRAIN - the paper darkened by 0.955, which is one pixel in eighteen of
 * the sheet - at 4.422, and `src/hud/legible.test.ts` measured the consequence on the delivered
 * frame: 932 of 17,924 writing pixels sit on a background under the bar, and every one of them
 * is wash on grain. It was the only contrast failure in the game.
 *
 * 0.56 clears it. MEASURED with the sRGB relative-luminance formula: wash on grain 4.559, wash
 * on paper 4.990, both above 4.5, and the failing set is now EMPTY rather than one pair.
 *
 * IT IS A CHANGE THE OWNER CAN REVERT ON SIGHT AND IT IS MEANT TO BE. The paper's own three
 * scalars are their ruling and "every one of those three scalars was SET BY LOOKING"; this is
 * the ink on it, moved 0.02 of a value step - the sheet was photographed before and after and
 * the paper is the same paper. If their eye says otherwise, the number to put back is 0.58 and
 * the test that pins it names the pair that comes back with it.
 */
export const CHART_WASH_RGB: Rgb = hsvToRgb(anchor.h, anchor.s * 0.9, anchor.v * 0.56);

/**
 * THE SHADOW THE CHART CASTS ON THE WATER, above its torn edge.
 *
 * Without it the strip reads as a shoreline the frame ends at rather than as a sheet lying
 * on the sea - which is the same failure as `everything-floats`, inverted: there, objects
 * had no contact with the water; here, the paper had none either.
 *
 * It is NOT a new number and it is not a re-ordering of one. It is SHADOW_RAMP EXACTLY as
 * living-sea measured it on the reference frames - nine steps outward from a contact,
 * x0.901 touching the object, dipping to x0.80 four pixels out, lightening to x0.94 - because
 * a chart lying on water is an object contacting water and gets the treatment the run already
 * measured for one. The first build spent only four of the nine and the sheet still read as a
 * shoreline; the shadow the reference actually has is nine pixels deep.
 */
export const CHART_CAST_RGBS: readonly Rgb[] = SHADOW_RGBS;

/** The material mask's two flat colours: what is water, and what is not. */
export const MASK_WATER_RGB: Rgb = [255, 255, 255];
export const MASK_LAND_RGB: Rgb = [0, 0, 0];

export function rgbHex([r, g, b]: Rgb): number {
  return (r << 16) | (g << 8) | b;
}

/** Rec. 709 luma, the same definition tools/instruments/png.mjs uses. */
export function luma([r, g, b]: Rgb): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// ---- THE TONE SEA'S COLOURS (round 9, CONTRACT 1.5w, `?water=render` only) ---------------------

/**
 * THE TONE RAMP, and every scalar in it is MEASURED off the owner's three renders rather than
 * chosen. `gauntlet/living-sea/render-target-20260910/measure.json` reads the modal colour of a
 * deep rectangle and a shelf rectangle in each, hand-drawn and looked at, and the three say the
 * same thing about what a shelf does to a sea colour:
 *
 *              deep -> shelf          hue          saturation        luma
 *   woolstrand [115,137,145] -> [136,164,168]   196.0 -> 187.5   x0.90   130.7 -> 172.2  (+41.5)
 *   blackstage [ 41, 73,110] -> [ 50, 89,126]   212.2 -> 209.2   x0.95    65.7 ->  78.6  (+12.9)
 *   almadra    [ 46, 93,147] -> [ 44, 90,137]   212.1 -> 210.3   x0.99    86.3 -> 110.1  (+23.8)
 *
 * So the LUMA does the work; the hue moves a little towards cyan (-1.8 to -8.5 deg) and the
 * saturation drops a little (x0.90 to x0.99). None of the three is anywhere near a hue jump, and
 * that is the whole reason the likeness instrument gates hue delta at all.
 *
 * THE DEEPEST TONE IS THE BASE, EXACTLY - `seaTone(seaBase(sea), 0) === seaBase(sea)` by
 * construction, not by round-trip luck - so `?sea=` keeps the meaning CONTRACT 1.5i(c) gave it and
 * moving the open-sea base stays the owner's ruling and not a side effect of this round.
 *
 * The lift is SOLVED BACK TO v rather than applied to it: at a fixed hue and saturation every
 * channel of the HSV->RGB conversion is linear in v, so the v that hits a target luma is one
 * division. Scaling v directly would make the same +105 mean a different brightness on every base.
 */
export const SEA_TONE_COUNT = 7;
/** MEASURED bracket -1.8..-8.5 deg over three renders; the far end, so the palest reads as shallow. */
export const SEA_TONE_HUE_SHIFT = -9;
/** MEASURED bracket x0.90..x0.99; a shade past the far end for the same reason. */
export const SEA_TONE_SAT_SCALE = 0.62;
/**
 * GUESS inside a MEASURED bracket: the renders' own deep-to-shelf lifts are +12.9, +23.8 and
 * +41.5 over rectangles that are BOTH shelf water by this game's reckoning, and woolstrand's
 * waterline itself is luma 206 against a deep 130.7 (+75). The ramp has to cover deep water to
 * beach, so +105 on a base of luma 80.6 (`?sea=0.5`) lands the palest tone at 185.6.
 */
export const SEA_TONE_LUMA_LIFT = 78;

/** (h, s, v=1) as unrounded RGB in 0..1 per unit of v: the linear coefficients of the conversion. */
function hsvUnit(h: number, s: number): readonly [number, number, number] {
  const hh = (((h % 360) + 360) % 360) / 60;
  const c = s;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = 1 - c;
  if (hh < 1) return [c + m, x + m, m];
  if (hh < 2) return [x + m, c + m, m];
  if (hh < 3) return [m, c + m, x + m];
  if (hh < 4) return [m, x + m, c + m];
  if (hh < 5) return [x + m, m, c + m];
  return [c + m, m, x + m];
}

/**
 * THE TONE at position `t` of the ramp, 0 deepest. `t = 0` returns `base` itself, byte for byte.
 * A `t` whose target luma is past what the shifted hue can reach at v = 255 lands on the brightest
 * colour that hue and saturation have, which is a clamp and is stated rather than hidden.
 */
export function seaTone(base: Rgb, t: number): Rgb {
  const k = Math.min(1, Math.max(0, t));
  if (k <= 0) return base;
  const { h, s } = rgbToHsv(base);
  const h2 = h + SEA_TONE_HUE_SHIFT * k;
  const s2 = s * (1 + (SEA_TONE_SAT_SCALE - 1) * k);
  const target = luma(base) + SEA_TONE_LUMA_LIFT * k;
  const u = hsvUnit(h2, s2);
  const perV = 0.2126 * u[0] + 0.7152 * u[1] + 0.0722 * u[2];
  const v = perV <= 0 ? 0 : Math.min(255, target / perV);
  return hsvToRgb(h2, s2, v);
}

/**
 * THE CREST, on a tone, at an amplitude. THE LIFT IS A FUNCTION OF `calmAmplitude` and so is the
 * crest's COVERAGE (sea.ts), which is what keeps the whole new layer inside the one scalar
 * `water.test.ts:639` and `arrival.mjs` already measure. If only the coverage moved, a shoreline
 * could flash at a fixed +45 luma with the calm suite green - risk 2 of the round, answered here.
 *
 * +20 at amplitude 0 and +50 at amplitude 1, on the LUMA, hue held and saturation halved: the
 * pale ripple lines of blackstage-8325 sit about +40 over their own water and are the same hue.
 * The amplitude is QUANTISED to CREST_LIFT_STEPS before it is used, so the crest is a small set
 * of hard values across the frame and not a continuous ramp (1.5e).
 *
 * -- THE ONE MEASURED GAP FROM THE RENDERS, AND WHY IT IS NOW A KNOB (CONTRACT 1.5w) -------------
 *
 * +20..+50 was a GUESS taken off blackstage's ripple lines, and it is the smallest lift of the
 * three references. MEASURED in gauntlet/living-sea/render-target-20260910/measure.json, the
 * crest of the reference the owner keeps pointing at is far stronger than that: woolstrand's
 * shelf rectangle puts 8.288% of its pixels more than 60 luma above the region's own median
 * (`brightFractionPct`, `brightOverP50` 60), and the game's render arm at scale 1 puts 1.722%
 * there (gauntlet/rounds/living-sea-r9/likeness-murrin-roost-render.json). The builder disclosed
 * the gap and a critic confirmed it, and the owner could not SEE a version of the other choice
 * because there was no parameter for it. Now there is one, and it defaults to today.
 *
 * `crestLift` SCALES the whole lift, base and span together: at 1 the crest is +20..+50 over its
 * own tone, at 2 it is +40..+100, at 0 it is the tone itself and the crest network vanishes as a
 * value while still costing its draws. It is a GUESS the owner rules on by eye, exactly as
 * `crestGain` and `crestPx` are. What it does NOT change is the hue (held) or the saturation
 * (halved), because the references' pale streaks are the same water with light on it.
 */
export const CREST_LIFT_BASE = 20;
export const CREST_LIFT_SPAN = 30;
export const CREST_SAT_SCALE = 0.5;
export const CREST_LIFT_STEPS = 8;
/** The lift scale that is today's sea, byte for byte. `?crestlift` moves it; water.ts `crestLift`. */
export const CREST_LIFT_SCALE = 1;
/**
 * The largest lift worth offering. The base tone's luma runs to about 180 at the pale end of the
 * ramp and white is 255, so a scale past this can only clamp: `crestFor` is already `Math.min(255,
 * target / perV)` and a bigger number would silently be the same picture. Stated, not hidden.
 */
export const CREST_LIFT_SCALE_MAX = 6;

export function crestFor(tone: Rgb, amplitude: number, lift: number = CREST_LIFT_SCALE): Rgb {
  const a = Math.round(Math.min(1, Math.max(0, amplitude)) * (CREST_LIFT_STEPS - 1)) / (CREST_LIFT_STEPS - 1);
  const k = Math.max(0, lift);
  const { h, s } = rgbToHsv(tone);
  const s2 = s * CREST_SAT_SCALE;
  const target = luma(tone) + (CREST_LIFT_BASE + CREST_LIFT_SPAN * a) * k;
  const u = hsvUnit(h, s2);
  const perV = 0.2126 * u[0] + 0.7152 * u[1] + 0.0722 * u[2];
  const v = perV <= 0 ? 0 : Math.min(255, target / perV);
  return hsvToRgb(h, s2, v);
}

/**
 * FOAM, at a shore of a given material (shelf.ts: 0 sand, 1 lip, 2 rock).
 *
 * MEASURED on woolstrand-8302, whose beach is the one clean foam in the three renders: the
 * transect's first water pixel off the sand is luma 206 and the whitest of the surf reaches 241.
 * blackstage-8325's rock has no foam at all - its shelf rectangle's bright fraction is 0.046% -
 * which is why rock's WIDTH is zero in contact.ts and this colour is never asked for there.
 *
 * The hue is the sea's own, held, and the saturation nearly gone: surf is the water, aerated.
 */
export function foamFor(base: Rgb, material: number): Rgb {
  const sand = material === 0;
  const { h, s } = rgbToHsv(base);
  const s2 = s * (sand ? 0.14 : 0.26);
  const target = sand ? 232 : 205;
  const u = hsvUnit(h, s2);
  const perV = 0.2126 * u[0] + 0.7152 * u[1] + 0.0722 * u[2];
  const v = perV <= 0 ? 0 : Math.min(255, target / perV);
  return hsvToRgb(h, s2, v);
}
